import React, { useState, useEffect } from 'react';
import { Card, Table, Typography, Space, Tag, Spin, Button, message, Modal, App } from 'antd';
import { Row, Col, Select, Input } from 'antd';

import { useTranslation } from 'react-i18next';
import { getConsultantBillingOverview, generateBillingInvoice, previewBillingInvoice } from '../services/loanService';
import Page from '../components/layout/Page';
import { Link, useNavigate } from 'react-router-dom';

import { formatCurrency } from '../utils/formatters';
import dayjs from 'dayjs';
import { EyeOutlined, PlusOutlined, SearchOutlined, DownloadOutlined, EditOutlined, ZoomInOutlined, FileExcelOutlined, DeleteOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';


const { Text } = Typography;

const ConsultantBillingOverviewPage = () => {
    const [loading, setLoading] = useState(true);
    const [billingGroups, setBillingGroups] = useState([]);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewContent, setPreviewContent] = useState('');
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await getConsultantBillingOverview();
                setBillingGroups(data);
            } catch (error) {
                console.error('Error fetching billing overview:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const expandedRowRender = (record) => {
        const columns = [
            {
                title: t('loan.fields.processNumber'),
                dataIndex: 'process_number',
                key: 'process_number',
            },
            {
                title: t('loan.fields.customer'),
                dataIndex: 'customer_name',
                key: 'customer_name',
            },
            {
                title: t('loan.fields.amount'),
                dataIndex: 'amount',
                key: 'amount',
                align: 'right',
                render: (amount) => formatCurrency(amount),
            },                 
            {
                title: t('loan.fields.type'),
                dataIndex: 'type',
                key: 'type',
                render: (type) => (
                    <Tag color={type === 'primary' ? 'blue' : 'green'}>
                        {type === 'primary' ? t('loan.consultant.primary') : t('loan.consultant.secondary')}
                    </Tag>
                ),
            },         
            {
                title: t('loan.fields.consultantShare'),
                dataIndex: 'consultant_share',
                key: 'consultant_share',
                align: 'right',
                render: (share) => `${share}%`,
            },
            {
                title: t('loan.fields.consultantProvision'),
                key: 'consultant_provision',
                align: 'right',
                render: (_, record) => formatCurrency(record.provision),
            },
            {
                title: t('loan.fields.extra_bonus'),
                dataIndex: 'extra_bonus',
                key: 'extra_bonus',
                align: 'right',
                render: (extra_bonus) => formatCurrency(extra_bonus),
            },  
            {
                title: t('loan.fields.payout_sum'),
                dataIndex: 'payout_sum',
                key: 'payout_sum',
                align: 'right',
                render: (payout_sum) => formatCurrency(payout_sum),
            },             
        ];

        return (
            <Table
                columns={columns}
                dataSource={record.loans}
                pagination={false}
                rowKey="id"
            />
        );
    };

    const generateInvoicePDF = async (record) => {
        try {
            await generateBillingInvoice(record);
            message.success(t('billing.overview.invoiceGenerated'));
        } catch (error) {
            console.error('Error generating invoice:', error);
            message.error(t('billing.overview.errorGeneratingInvoice'));
        }
    };

    const previewInvoice = async (record) => {
        try {
            const html = await previewBillingInvoice(record);
            setPreviewContent(html);
            setPreviewVisible(true);
        } catch (error) {
            console.error('Error previewing invoice:', error);
            message.error(t('billing.overview.errorPreviewingInvoice'));
        }
    };

    const columns = [
        {
            title: t('common.actions'),
            key: 'actions',
            align: 'left',
            render: (_, record) => (
                <Space>
                    <Button 
                        icon={<EyeOutlined />} 
                        onClick={() => previewInvoice(record)} 
                        title={t('billing.overview.preview')}
                    />
                    <Button 
                        type="primary"
                        icon={<DownloadOutlined />} 
                        onClick={() => generateInvoicePDF(record)}
                        title={t('billing.overview.downloadInvoice')}
                    />
                </Space>
            ),
        },        
        {
            title: t('loan.fields.billingNumber'),
            dataIndex: 'billing_number',
            key: 'billing_number',
        },     
        {
            title: t('loan.fields.billingDate'),
            dataIndex: 'billing_date',
            key: 'billing_date',
            render: (date) => dayjs(date).format('DD.MM.YYYY'),
        },
        {
            title: t('loan.fields.loanCount'),
            dataIndex: 'loan_count',
            key: 'loan_count',
            align: 'right',
        },        {
            title: t('loan.fields.totalAmount'),
            dataIndex: 'total_amount',
            key: 'total_amount',
            align: 'right',
            render: (amount) => formatCurrency(amount),
        },
        {
            title: t('loan.fields.totalProvision'),
            dataIndex: 'total_provision',
            key: 'total_provision',
            align: 'right',
            render: (provision) => formatCurrency(provision),
        },
        {
            title: t('loan.fields.totalExtraBonus'),
            dataIndex: 'total_extra_bonus',
            key: 'total_extra_bonus',
            align: 'right',
            render: (total_extra_bonus) => formatCurrency(total_extra_bonus),
        },    
        {
            title: t('loan.fields.totalInvoiceSum'),
            dataIndex: 'total_sum',
            key: 'total_sum',
            align: 'right',
            render: (sum) => formatCurrency(sum),
        }
    ];

    if (loading) {
        return (
            <App>
                <Page title={t('billing.overview.title')}>
                    <Space align="center" size="large">
                        <Spin size="large" />
                        <span>{t('common.loading')}</span>
                    </Space>
                </Page>
            </App>
        );
    }

    const headerExtra = (
        <Row gutter={[8, 8]} align="middle">
            <Col xs={24} sm={24} md={12} lg={8}>
                <Button type="primary" onClick={() => navigate('/billing/new')}>
                {t('billing.addInvoice')}
                </Button>
            </Col>
        </Row>
    );    

    return (
        <App>
            <Page 
                title={t('billing.overview.title')}
                subtitle={t('billing.overview.subtitle')}
                headerExtra={headerExtra}
            >
                <div>
                    <Table
                        columns={columns}
                        dataSource={billingGroups}
                        expandable={{
                            expandedRowRender,
                            expandRowByClick: true,
                        }}
                        rowKey="billing_number"
                        pagination={false}
                    />
                </div>
                <Modal
                    title={t('billing.overview.preview')}
                    open={previewVisible}
                    onCancel={() => setPreviewVisible(false)}
                    width="1024px"
                    footer={[
                        <Button key="close" onClick={() => setPreviewVisible(false)}>
                            {t('common.close')}
                        </Button>
                    ]}
                >
                    <div dangerouslySetInnerHTML={{ __html: previewContent }} />
                </Modal>
            </Page>
        </App>
    );
};

export default ConsultantBillingOverviewPage;
